import { useEffect, useState } from 'react'
import { loadStripe } from '@stripe/stripe-js'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { useAiPacks, useBuyAiPack, type AiPack } from '@/api/billing'
import { montantCad } from '@/lib/receptionniste'
import { UpdateCardDialog } from './UpdateCardDialog'

const stripePromise = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY)
  : null

type Mode = 'monthly' | 'onetime'

/**
 * Achat d'un pack de minutes IA, sans quitter le dashboard : quatre packs,
 * mensuel (ligne ajoutée à l'abonnement) ou ponctuel (minutes prépayées).
 * Sans carte enregistrée, la fenêtre « Mettre à jour la carte » s'ouvre
 * d'abord, puis l'achat repart tout seul.
 */
export function BuyAiPackDialog({ open, onClose, darkMode }: { open: boolean; onClose: () => void; darkMode: boolean }) {
  const { t, i18n } = useTranslation()
  const { data, isLoading } = useAiPacks(open)
  const buy = useBuyAiPack()
  const [mode, setMode] = useState<Mode>('onetime')
  const [choix, setChoix] = useState<string | null>(null)
  const [carte, setCarte] = useState(false)
  const [enCours, setEnCours] = useState(false)

  const packs = data?.packs ?? []
  const mensuelPossible = data?.can_subscribe_monthly ?? false
  // La réceptionniste d'abord quand un abonnement le permet
  useEffect(() => {
    if (open) setMode(mensuelPossible ? 'monthly' : 'onetime')
  }, [open, mensuelPossible])
  const prix = (p: AiPack) => (mode === 'monthly' ? p.price_cad_monthly : p.price_cad_onetime)
  // Forfait annuel : la réceptionniste se paie à l'année, 20 % de moins (2 nov. 2026).
  const annuel = mode === 'monthly' && data?.billing_interval === 'year'
  const prixAnnuel = (p: AiPack) => (annuel && p.yearly_available && p.price_cad_yearly ? Number(p.price_cad_yearly) : null)

  const acheter = async () => {
    if (!choix) return
    setEnCours(true)
    try {
      const r = await buy.mutateAsync({ pack: choix, mode })
      if (r.requires_card) {
        setCarte(true)
        return
      }
      if (r.requires_action && r.client_secret && stripePromise) {
        const stripe = await stripePromise
        const res = await stripe?.confirmCardPayment(r.client_secret)
        if (res?.error) throw new Error(res.error.message)
        // Le webhook crédite ; on rafraîchit dans un instant.
        toast.success(t('manager.billing.packs.bought', 'Pack ajouté. Vos minutes seront visibles dans un instant.'))
        onClose()
        return
      }
      if (r.error) throw new Error(r.message || r.error)
      toast.success(
        mode === 'monthly'
          ? t('manager.billing.packs.boughtMonthly', 'Pack ajouté à votre abonnement.')
          : t('manager.billing.packs.boughtOnetime', '{{minutes}} minutes prépayées ajoutées.', { minutes: r.credit?.minutes ?? 0 }),
      )
      onClose()
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string; error?: string } }; message?: string })
      toast.error(msg.response?.data?.message || msg.response?.data?.error || msg.message || t('manager.billing.packs.error', 'Achat impossible'))
    } finally {
      setEnCours(false)
    }
  }

  return (
    <>
      <Dialog open={open && !carte} onOpenChange={(o) => !o && !enCours && onClose()}>
        <DialogContent className={cn('sm:max-w-2xl', darkMode && 'bg-slate-900 border-slate-700 text-slate-100')}>
          <DialogHeader>
            <DialogTitle>{t('manager.billing.packs.title', 'Réceptionniste IA et minutes')}</DialogTitle>
            <DialogDescription>
              {t('manager.billing.packs.desc', 'Une réceptionniste pour toute l’équipe, autant de profils que vous voulez.')}
            </DialogDescription>
          </DialogHeader>

          <ToggleGroup type="single" value={mode} onValueChange={(v) => v && setMode(v as Mode)} className="justify-start">
            <ToggleGroupItem value="monthly" disabled={!mensuelPossible}>{t('manager.billing.packs.monthly', 'Réceptionniste')}</ToggleGroupItem>
            <ToggleGroupItem value="onetime">{t('manager.billing.packs.onetime', 'Minutes prépayées')}</ToggleGroupItem>
          </ToggleGroup>
          <p className={cn('text-xs -mt-2', darkMode ? 'text-slate-400' : 'text-slate-500')}>
            {mode === 'onetime'
              ? t('manager.billing.packs.onetimeHelp', 'Achetées une fois, sans expiration, utilisées quand les minutes du mois sont épuisées.')
              : annuel
                ? t('manager.billing.packs.yearlyHelp', "Sur votre facture annuelle, 20 % de moins. Aujourd'hui, vous payez le temps qui reste jusqu'au renouvellement.")
                : t('manager.billing.packs.monthlyHelp', 'Chaque mois, sur votre facture. Changez de taille ou résiliez quand vous voulez.')}
          </p>

          {isLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
          ) : (
            <div className={cn('grid grid-cols-2 gap-3', mode === 'monthly' ? 'sm:grid-cols-3' : 'sm:grid-cols-4')}>
              {packs.filter((p) => mode === 'onetime' || p.monthly_available).map((p) => {
                const actif = choix === p.slug
                const parAn = prixAnnuel(p)
                return (
                  <button
                    key={p.slug}
                    type="button"
                    onClick={() => setChoix(p.slug)}
                    className={cn(
                      'rounded-lg border p-4 text-left transition-colors whitespace-nowrap',
                      actif
                        ? darkMode ? 'border-slate-100' : 'border-slate-900'
                        : darkMode ? 'border-slate-700 hover:border-slate-500' : 'border-slate-200 hover:border-slate-400',
                    )}
                  >
                    <div className="text-sm">{mode === 'monthly' ? p.monthly_name : p.name}</div>
                    <div className="mt-2 text-2xl font-semibold">
                      {parAn !== null
                        ? montantCad(parAn / 12, i18n.language)
                        : Number(prix(p)).toLocaleString('fr-CA', { maximumFractionDigits: 0 })} $
                      <span className={cn('text-xs font-normal ml-1', darkMode ? 'text-slate-400' : 'text-slate-500')}>
                        {mode === 'monthly' ? t('manager.billing.packs.perMonth', '/mois') : t('manager.billing.packs.once', 'une fois')}
                      </span>
                    </div>
                    {parAn !== null && (
                      <div className={cn('text-xs', darkMode ? 'text-slate-400' : 'text-slate-500')}>
                        {t('manager.billing.packs.perYear', '{{price}} $ par an', { price: montantCad(parAn, i18n.language) })}
                      </div>
                    )}
                    <div className={cn('mt-3 text-sm', darkMode ? 'text-slate-400' : 'text-slate-600')}>{p.minutes} min</div>
                  </button>
                )
              })}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose} disabled={enCours}>{t('common.cancel', 'Annuler')}</Button>
            <Button type="button" onClick={() => void acheter()} disabled={!choix || enCours}>
              {enCours && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {t('manager.billing.packs.confirm', 'Acheter')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UpdateCardDialog
        open={carte}
        onClose={() => setCarte(false)}
        description={t('manager.billing.packs.cardFirst', 'Enregistrez une carte pour acheter le pack.')}
        onSuccess={async () => {
          setCarte(false)
          await acheter()
        }}
      />
    </>
  )
}
